<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'

interface Props {
  isOpen: boolean
  mode: 'create' | 'rename'
  initialName?: string
  parentName?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'submit', name: string): void
}

const props = withDefaults(defineProps<Props>(), {
  initialName: '',
  parentName: '',
  loading: false,
})

const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const formRef = ref()
const folderName = ref('')

const title = () => (props.mode === 'rename' ? 'Переименовать папку' : 'Создать папку')
const submitLabel = () => (props.mode === 'rename' ? 'Сохранить' : 'Создать')

const rules = {
  name: [
    (v: string) => !!v?.trim() || 'Название обязательно',
    (v: string) => (v?.trim().length ?? 0) <= 255 || 'Не больше 255 символов',
  ],
}

const closeModal = () => {
  if (props.loading) {
    return
  }

  emit('update:isOpen', false)
}

watch(
  () => props.isOpen,
  async open => {
    if (!open) {
      return
    }

    folderName.value = props.initialName
    await nextTick()
    formRef.value?.resetValidation()
  },
)

watch(
  () => props.initialName,
  value => {
    if (props.isOpen) {
      folderName.value = value
    }
  },
)

const onSubmit = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  emit('submit', folderName.value.trim())
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 480"
    :fullscreen="smAndDown"
    persistent
    @update:model-value="emit('update:isOpen', $event)"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-6">
        <span class="text-h5 font-weight-bold">{{ title() }}</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <p
        v-if="mode === 'create' && parentName"
        class="text-body-2 text-medium-emphasis mb-4"
      >
        Папка будет создана в «{{ parentName }}»
      </p>

      <VForm
        ref="formRef"
        @submit.prevent="onSubmit"
      >
        <VTextField
          v-model="folderName"
          variant="outlined"
          density="compact"
          :rules="rules.name"
          maxlength="255"
          autofocus
        >
          <template #label>
            <span>Название папки<sup class="required-label">*</sup></span>
          </template>
        </VTextField>

        <div class="d-flex justify-end gap-4 mt-6">
          <VBtn
            variant="outlined"
            color="grey"
            class="px-6"
            :disabled="loading"
            @click="closeModal"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="px-6"
            :loading="loading"
            @click="onSubmit"
          >
            {{ submitLabel() }}
          </VBtn>
        </div>
      </VForm>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.required-label {
  color: rgb(var(--v-theme-error));
  margin-inline-start: 2px;
}

:deep(.v-card) {
  border-radius: 12px;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
}
</style>
